import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { EntityInfoField } from '../../../../../../components/entities/EntityInfoField';
import { styleSheet } from '../styles';
import { Address } from './Address/Address';
import { EmailList } from './Email/EmailList';
import { useContactTagsUtils } from './hooks/useContactTagsUtils';
import { PhoneNumberList } from './PhoneNumber/PhoneNumberList';
import { WebsiteList } from './Website/WebsiteList';

type Props = {
	contact: Api.IContact;
	compactLayout?: boolean;
	showSendMessage?: boolean;
	onEmailChanged: (emails: Api.EmailAddress[]) => void;
	onPhoneNumberChanged: (phoneNumbers: Api.IPhoneNumber[]) => void;
	onAddressChanged: (address: Api.IAddress) => void;
	onWebsiteChanged: (website: string) => void;
	refetchContact: () => void;
};

export const ContactDetails = ({
	contact,
	onEmailChanged,
	onPhoneNumberChanged,
	onAddressChanged,
	onWebsiteChanged,
	refetchContact,
}: Props) => {
	const userSession = useUserSession();
	const { onRequestAddTag, onRequestRemoveTag, isLoading } = useContactTagsUtils(contact, refetchContact);

	// Create a contact view model from the contact prop
	const contactVm = React.useMemo(() => {
		return new Api.ContactViewModel(userSession, contact);
	}, [contact, userSession]);

	// Only explicit false locks this — a live integration owns the unsubscribe; undefined (older API) stays editable.
	const shouldDisableUnsubscribeCheckbox = contact.allowUnsubscribeEdit === false;

	const onUnsubscribeFromEmailCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = !!(e.target as HTMLInputElement).checked;
		if (checked) {
			onRequestAddTag({ name: 'Unsubscribe' });
		} else {
			onRequestRemoveTag({ name: 'Unsubscribe' });
		}
	};

	const renderLabeledField = (label: string, value: React.ReactNode, valueClassName?: string, truncateText = true) => {
		return (
			<EntityInfoField
				enableLinkify={true}
				label={label}
				truncateText={truncateText}
				value={value}
				valueClassName={valueClassName}
			/>
		);
	};

	return (
		<div className={css(styleSheet.contactDetailsContainer)}>
			<EmailList
				contact={contact}
				contactVm={contactVm}
				onEmailChanged={onEmailChanged}
				onUnsubscribeFromEmailCheckboxChanged={onUnsubscribeFromEmailCheckboxChanged}
				isUnsubscribing={isLoading}
				disableUnsubscribeCheckbox={shouldDisableUnsubscribeCheckbox}
			/>

			<PhoneNumberList
				contact={contact}
				contactVm={contactVm}
				onPhoneNumberChanged={onPhoneNumberChanged}
				refetchContact={refetchContact}
			/>

			<Address contact={contact} onAddressChanged={onAddressChanged} />

			<WebsiteList contact={contact} onWebsiteChanged={onWebsiteChanged} />

			{/* Misc Section */}
			<div className={css(styleSheet.contactDetailSection)}>
				{renderLabeledField('Misc', contactVm.bio, css(styleSheet.contactDetailBio), false)}
			</div>
		</div>
	);
};
